import React, { useCallback, useRef, useState, useEffect, memo, useMemo } from 'react';
import { GoogleMap, useJsApiLoader, Polygon, Marker, Polyline, OverlayView, Circle } from '@react-google-maps/api';

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
const libraries = ['geometry'];

const mapContainerStyle = { width: '100%', height: '100%' };

const mapOptions = {
  disableDefaultUI: true,
  zoomControl: true,
  mapTypeControl: true,
  mapTypeControlOptions: { position: 3 }, // TOP_RIGHT
  streetViewControl: false,
  fullscreenControl: false,
  clickableIcons: false,
  gestureHandling: 'greedy',
  styles: [
    { featureType: 'poi.business', stylers: [{ visibility: 'off' }] },
    { featureType: 'transit', stylers: [{ visibility: 'off' }] }
  ]
};

// Grid section colors - distinct, high contrast colors
const GRID_COLORS = [
  '#2563eb', // Blue
  '#dc2626', // Red
  '#16a34a', // Green
  '#ca8a04', // Yellow/Gold
  '#9333ea', // Purple
  '#ea580c', // Orange
  '#0d9488', // Teal
  '#be185d', // Pink
  '#4f46e5', // Indigo
  '#65a30d', // Lime
];

// Drawing path polyline - shows the path being drawn as user clicks points
// When drawing: shows open path (no closing line back to start)
// When not drawing: shows closed outline path
const DrawingPath = memo(({ points, isDrawing, color }) => {
  if (points.length < 2) return null;
  
  // Convert points to Google Maps format
  const path = points.map(p => ({ lat: p[0], lng: p[1] }));
  
  // If not drawing and we have 3+ points, close the path by adding first point at end
  const displayPath = (!isDrawing && points.length >= 3) 
    ? [...path, path[0]]  // Close the loop
    : path;               // Keep open while drawing
  
  return (
    <Polyline
      path={displayPath}
      options={{
        strokeColor: color || '#8b5cf6',
        strokeOpacity: isDrawing ? 0.8 : 1,
        strokeWeight: isDrawing ? 3 : 2,
        geodesic: true,
        zIndex: 800,
        icons: isDrawing ? [{
          icon: {
            path: 'M 0,-1 0,1',
            strokeOpacity: 1,
            scale: 3
          },
          offset: '0',
          repeat: '15px'
        }] : []
      }}
    />
  );
});

// Simple vertex marker for polygon corners - always draggable
const VertexMarker = memo(({ position, index, onDrag, isFirstPoint, canClose, onClose }) => (
  <Marker
    position={position}
    draggable={!canClose || !isFirstPoint}  // Don't drag the close marker when it can close
    onDragEnd={(e) => onDrag(index, e.latLng.lat(), e.latLng.lng())}
    onClick={() => {
      // If this is the first point and we can close, trigger close
      if (isFirstPoint && canClose && onClose) {
        onClose();
      }
    }}
    icon={{
      path: window.google?.maps?.SymbolPath?.CIRCLE,
      scale: isFirstPoint && canClose ? 14 : 8,
      fillColor: isFirstPoint && canClose ? '#22c55e' : '#8b5cf6',
      fillOpacity: 1,
      strokeColor: isFirstPoint && canClose ? '#16a34a' : '#ffffff',
      strokeWeight: isFirstPoint && canClose ? 3 : 2
    }}
    label={{ 
      text: isFirstPoint && canClose ? '✓' : String(index + 1), 
      color: '#fff', 
      fontSize: isFirstPoint && canClose ? '16px' : '11px', 
      fontWeight: 'bold' 
    }}
    title={isFirstPoint && canClose ? 'Click to close polygon' : `Point ${index + 1}`}
    zIndex={isFirstPoint && canClose ? 1100 : 1000}
    cursor={isFirstPoint && canClose ? 'pointer' : 'move'}
  />
));

// Clickable edge polyline - allows inserting points by clicking on the line
const ClickableEdge = memo(({ start, end, afterIndex, onInsert }) => {
  const path = [
    { lat: start[0], lng: start[1] },
    { lat: end[0], lng: end[1] }
  ];

  const handleClick = useCallback((e) => {
    // Get the click position on the line
    const clickLat = e.latLng.lat();
    const clickLng = e.latLng.lng();
    onInsert(afterIndex, { lat: clickLat, lng: clickLng });
  }, [afterIndex, onInsert]);

  return (
    <Polyline
      path={path}
      options={{
        strokeColor: '#8b5cf6',
        strokeOpacity: 0, // Invisible but clickable
        strokeWeight: 20, // Wide clickable area
        clickable: true,
        zIndex: 500
      }}
      onClick={handleClick}
    />
  );
});

// Midpoint marker for edge insertion (visual indicator)
const MidpointMarker = memo(({ position, afterIndex, onInsert }) => (
  <Marker
    position={position}
    onClick={() => onInsert(afterIndex, position)}
    icon={{
      path: window.google?.maps?.SymbolPath?.CIRCLE,
      scale: 6,
      fillColor: '#10b981',
      fillOpacity: 0.9,
      strokeColor: '#ffffff',
      strokeWeight: 2
    }}
    title="Click to add point here"
    zIndex={800}
  />
));

// Zone polygon — clickable so the user can pop a small floating trash
// button to delete an accidentally-drawn shape (the "accidentally drew
// two zones" case). When clicked, the parent shows the trash overlay at
// the click position. Visual-only when the parent disables clickability
// (e.g. while polygon-drawing or radius-draw modes are active so we
// don't steal the map click).
const ZonePolygon = memo(({ paths, isActive, clickable, onPolygonClick }) => (
  <Polygon
    paths={paths}
    options={{
      fillColor: isActive ? '#8b5cf6' : '#6366f1',
      fillOpacity: isActive ? 0.25 : 0.15,
      strokeColor: isActive ? '#8b5cf6' : '#6366f1',
      strokeWeight: isActive ? 3 : 2,
      clickable: !!clickable,
      zIndex: 100,
    }}
    onClick={clickable && onPolygonClick ? (e) => onPolygonClick(e) : undefined}
  />
));

// Exclusion polygon — drawn INSIDE a delivery zone to deselect a chunk
// of houses (e.g. a customer marked an apartment complex as no-delivery).
// Red fill keeps them visually distinct from delivery polygons. Rendered
// at a HIGHER zIndex than ZonePolygon so the red overlay always wins
// the visual hierarchy.
const ExclusionPolygon = memo(({ paths, clickable, onPolygonClick }) => (
  <Polygon
    paths={paths}
    options={{
      fillColor: '#ef4444',
      fillOpacity: 0.35,
      strokeColor: '#dc2626',
      strokeWeight: 2,
      strokeOpacity: 0.95,
      clickable: !!clickable,
      zIndex: 150,
    }}
    onClick={clickable && onPolygonClick ? (e) => onPolygonClick(e) : undefined}
  />
));

// Grid section polygon with prominent label
const GridSectionPolygon = memo(({ section, colorIndex, showLabel = true }) => {
  const color = GRID_COLORS[colorIndex % GRID_COLORS.length];
  const paths = section.boundary.map(p => ({ lat: p[0], lng: p[1] }));
  
  return (
    <>
      <Polygon
        paths={paths}
        options={{
          fillColor: color,
          fillOpacity: 0.15,
          strokeColor: color,
          strokeWeight: 3,
          strokeOpacity: 1,
          clickable: false,
          zIndex: 50
        }}
      />
      {showLabel && section.center && (
        <Marker
          position={{ lat: section.center[0], lng: section.center[1] }}
          icon={{
            url: `data:image/svg+xml,${encodeURIComponent(`
              <svg xmlns="http://www.w3.org/2000/svg" width="60" height="36">
                <rect x="0" y="0" width="60" height="36" rx="4" fill="${color}" opacity="0.95"/>
                <text x="30" y="14" font-family="Arial, sans-serif" font-size="11" font-weight="bold" fill="white" text-anchor="middle" dominant-baseline="middle">${section.label}</text>
                <text x="30" y="28" font-family="Arial, sans-serif" font-size="10" fill="white" text-anchor="middle" dominant-baseline="middle">${section.houseCount}</text>
              </svg>
            `)}`,
            anchor: { x: 30, y: 18 },
            scaledSize: { width: 60, height: 36 }
          }}
          zIndex={200}
        />
      )}
    </>
  );
});

// Blocked address marker (simple red marker)
const BlockedMarker = memo(({ position, address }) => (
  <Marker
    position={position}
    title={`DO NOT DELIVER: ${address}`}
    icon={{
      path: window.google?.maps?.SymbolPath?.CIRCLE,
      scale: 10,
      fillColor: '#dc2626',
      fillOpacity: 0.9,
      strokeColor: '#ffffff',
      strokeWeight: 2
    }}
  />
));

/**
 * DeliveryZoneMap - Google Maps for zone creation with grid overlay
 * Features:
 * - Draw polygon by clicking points (when drawing mode ON)
 * - Click on polygon edges to insert new points (always available)
 * - Drag polygon vertices to adjust (always available)
 * - Display grid sections with house counts
 */
// Optimized canvas-based house dots overlay with viewport culling and batched rendering
const HouseDotsOverlay = memo(({ map, houseLocations }) => {
  const overlayRef = useRef(null);
  const rafRef = useRef(null);
  const latLngCacheRef = useRef(null);

  // Pre-compute LatLng objects once when houseLocations change
  useEffect(() => {
    if (!window.google || !houseLocations.length) {
      latLngCacheRef.current = null;
      return;
    }
    latLngCacheRef.current = houseLocations
      .filter(h => h.lat && h.lon)
      .map(h => ({
        lat: h.lat,
        lon: h.lon,
        latLng: new window.google.maps.LatLng(h.lat, h.lon)
      }));
  }, [houseLocations]);

  useEffect(() => {
    if (!map || !houseLocations.length || !window.google) return;

    class DotsOverlay extends window.google.maps.OverlayView {
      constructor() {
        super();
        this.canvas = document.createElement('canvas');
        this.canvas.style.position = 'absolute';
        this.canvas.style.pointerEvents = 'none';
        this.canvas.style.zIndex = '999';
      }

      onAdd() {
        // Use overlayMouseTarget pane (higher z-order than overlayLayer where polygons render)
        const panes = this.getPanes();
        panes.overlayMouseTarget.appendChild(this.canvas);
      }

      draw() {
        const projection = this.getProjection();
        if (!projection) return;
        const bounds = this.getMap().getBounds();
        if (!bounds) return;
        const cached = latLngCacheRef.current;
        if (!cached || !cached.length) return;

        const ne = projection.fromLatLngToDivPixel(bounds.getNorthEast());
        const sw = projection.fromLatLngToDivPixel(bounds.getSouthWest());
        const width = Math.abs(ne.x - sw.x);
        const height = Math.abs(ne.y - sw.y);

        this.canvas.width = width;
        this.canvas.height = height;
        this.canvas.style.left = `${sw.x}px`;
        this.canvas.style.top = `${ne.y}px`;
        this.canvas.style.width = `${width}px`;
        this.canvas.style.height = `${height}px`;

        const ctx = this.canvas.getContext('2d');
        ctx.clearRect(0, 0, width, height);

        // Fast viewport culling via lat/lon range (avoids per-point bounds.contains)
        const bne = bounds.getNorthEast();
        const bsw = bounds.getSouthWest();
        const minLat = bsw.lat();
        const maxLat = bne.lat();
        const minLon = bsw.lng();
        const maxLon = bne.lng();
        const offX = sw.x;
        const offY = ne.y;

        // Pixel-level dedup grid to skip overlapping dots at low zoom
        const zoom = this.getMap().getZoom();
        const cellSize = zoom < 14 ? 6 : 0; // dedup when zoomed out
        const seen = cellSize > 0 ? new Set() : null;

        // Batch: single fill pass then single stroke pass
        const dotRadius = zoom >= 16 ? 6 : zoom >= 14 ? 5 : zoom >= 12 ? 4 : 3;
        const points = [];

        for (let i = 0, len = cached.length; i < len; i++) {
          const h = cached[i];
          if (h.lat < minLat || h.lat > maxLat || h.lon < minLon || h.lon > maxLon) continue;

          const pixel = projection.fromLatLngToDivPixel(h.latLng);
          const x = pixel.x - offX;
          const y = pixel.y - offY;

          if (seen) {
            const key = ((x / cellSize) | 0) * 10000 + ((y / cellSize) | 0);
            if (seen.has(key)) continue;
            seen.add(key);
          }

          points.push(x, y);
        }

        // Draw all fills in one pass
        ctx.fillStyle = '#22c55e';
        ctx.beginPath();
        for (let i = 0, len = points.length; i < len; i += 2) {
          ctx.moveTo(points[i] + dotRadius, points[i + 1]);
          ctx.arc(points[i], points[i + 1], dotRadius, 0, 6.2832);
        }
        ctx.fill();

        // Draw all strokes in one pass
        ctx.strokeStyle = '#ffffff';
        ctx.lineWidth = 1;
        ctx.beginPath();
        for (let i = 0, len = points.length; i < len; i += 2) {
          ctx.moveTo(points[i] + dotRadius, points[i + 1]);
          ctx.arc(points[i], points[i + 1], dotRadius, 0, 6.2832);
        }
        ctx.stroke();
      }

      onRemove() {
        if (this.canvas.parentNode) {
          this.canvas.parentNode.removeChild(this.canvas);
        }
      }
    }

    const overlay = new DotsOverlay();
    overlay.setMap(map);
    overlayRef.current = overlay;

    return () => {
      overlay.setMap(null);
      overlayRef.current = null;
    };
  }, [map, houseLocations]);

  // Throttled redraw on map interaction via requestAnimationFrame
  useEffect(() => {
    if (!map || !overlayRef.current) return;

    const scheduleRedraw = () => {
      if (rafRef.current) return;
      rafRef.current = requestAnimationFrame(() => {
        rafRef.current = null;
        overlayRef.current?.draw();
      });
    };

    const listeners = [
      map.addListener('bounds_changed', scheduleRedraw),
    ];

    return () => {
      listeners.forEach(l => window.google.maps.event.removeListener(l));
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [map]);

  return null;
});

const DeliveryZoneMap = ({
  initialCenter = { lat: 29.7604, lng: -95.3698 },
  initialZoom = 14,
  isDrawing = false,
  zonePoints = [],
  // Full list of all zones (active + inactive). When provided we render
  // every polygon ≥3 points on the map and make each clickable to pop a
  // small trash button — so the admin can delete an accidentally-drawn
  // shape directly from the map instead of hunting for the X chip above.
  // Falls back to rendering just `zonePoints` (legacy behavior) when not
  // provided.
  allPolygons = null,
  activePolygonIndex = 0,
  onDeletePolygon,
  onAddPoint,
  onDragPoint,
  onInsertPoint,
  onClosePolygon,
  blockedAddresses = [],
  gridSections = [],
  showGrids = true,
  centerTrigger = 0,
  houseLocations = [],
  // ---- Radius Draw mode. The map OWNS the preview state internally
  // (1st click → center, mouse-move → previewMeters, 2nd click → finalize)
  // so the parent never re-renders during the drag. The parent receives
  // the center via `onRadiusSetCenter` and the final radius via
  // `onRadiusFinalize` — both rare events. The optional `onRadiusPreview`
  // callback exists for the rare consumer that wants the live miles for
  // a side panel; it's called on every RAF tick so the consumer must
  // throttle its own work if it wants to avoid expensive re-renders.
  radiusDrawMode = false,
  onRadiusSetCenter,              // (lat, lng) => void
  onRadiusPreview,                // (meters) => void (optional, RAF-ticked)
  onRadiusFinalize,               // (lat, lng, meters) => void
  // ---- Exclusion Zone draw mode. Mirrors the polygon draw flow but
  // routes points into a separate state owned by the parent
  // (`exclusionPoints`). When `exclusionDrawingMode` is true, map clicks
  // call `onExclusionAddPoint`, and clicking near the first vertex
  // closes the polygon via `onCloseExclusionPolygon`. Each completed
  // exclusion polygon is passed in `exclusionPolygons` and rendered in
  // red, optionally clickable for delete.
  exclusionDrawingMode = false,
  exclusionPoints = [],
  exclusionPolygons = [],
  onExclusionAddPoint,            // (lat, lng) => void
  onCloseExclusionPolygon,        // () => void
  onDeleteExclusionPolygon,       // (idx) => void
}) => {
  const [radiusCenter, setRadiusCenterLocal] = useState(null);
  const [radiusPreviewMeters, setRadiusPreviewMetersLocal] = useState(0);
  // Floating trash button after a polygon click. Declared next to the
  // other map-overlay states so the dismiss useEffect below can reference
  // its setter without a TDZ violation.
  const [pendingDelete, setPendingDelete] = useState(null); // { idx, lat, lng } | null
  // IMPORTANT: this preview state lives INSIDE the map component (not in
  // the parent) because the parent (AdminCreateJob) is a ~3,700-line tree
  // and re-rendering it on every mouse-pixel was the root cause of the
  // "Radius Draw freezes with every click" bug. Don't lift this state up
  // without first lifting the whole render path along with it.
  // Reset internal state whenever the parent flips off radius-draw mode
  // so a re-activation starts clean.
  useEffect(() => {
    if (!radiusDrawMode) {
      setRadiusCenterLocal(null);
      setRadiusPreviewMetersLocal(0);
    }
  }, [radiusDrawMode]);

  // Dismiss any pending-delete overlay the moment the user enters a
  // drawing mode — otherwise the floating trash sits there confusingly
  // while they're trying to draw a new zone.
  useEffect(() => {
    if (isDrawing || radiusDrawMode || exclusionDrawingMode) setPendingDelete(null);
  }, [isDrawing, radiusDrawMode, exclusionDrawingMode]);
  const mapRef = useRef(null);
  const [hasInitialized, setHasInitialized] = useState(false);
  const lastCenterTrigger = useRef(centerTrigger);
  const [mapCenter, setMapCenter] = useState(initialCenter);

  const { isLoaded } = useJsApiLoader({
    id: 'google-maps-loader',
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    libraries
  });

  const onLoad = useCallback((map) => {
    mapRef.current = map;
    setHasInitialized(true);
  }, []);

  // Only re-center when centerTrigger changes
  useEffect(() => {
    if (centerTrigger !== lastCenterTrigger.current) {
      setMapCenter(initialCenter);
      if (mapRef.current) {
        mapRef.current.panTo(initialCenter);
      }
      lastCenterTrigger.current = centerTrigger;
    }
  }, [centerTrigger, initialCenter]);

  // ---- Trash-icon-on-polygon-click state ----
  // When the user clicks a polygon (and the map is NOT in a drawing mode
  // that would steal the click), we remember which polygon + where they
  // clicked, then float a tiny <OverlayView> with a delete button at that
  // spot. Any subsequent click on the blank map (no drawing mode) clears
  // the selection without committing a delete.
  // (The `pendingDelete` useState is declared higher up with the other
  // overlay state to keep the dismiss-on-drawing useEffect TDZ-safe.)
  const handlePolygonClick = useCallback((idx, e) => {
    // Only show the trash UI when no other mode is active that would
    // otherwise consume the click (polygon-drawing, radius-draw, or
    // exclusion-draw).
    if (isDrawing || radiusDrawMode || exclusionDrawingMode) return;
    if (!onDeletePolygon) return;
    if (!e || !e.latLng) return;
    setPendingDelete({
      kind: 'delivery',
      idx,
      lat: e.latLng.lat(),
      lng: e.latLng.lng(),
    });
  }, [isDrawing, radiusDrawMode, exclusionDrawingMode, onDeletePolygon]);

  // Same trash-icon flow but for an EXCLUSION polygon. Stored with a
  // separate `kind` so the OverlayView knows which delete handler to
  // call.
  const handleExclusionPolygonClick = useCallback((idx, e) => {
    if (isDrawing || radiusDrawMode || exclusionDrawingMode) return;
    if (!onDeleteExclusionPolygon) return;
    if (!e || !e.latLng) return;
    setPendingDelete({
      kind: 'exclusion',
      idx,
      lat: e.latLng.lat(),
      lng: e.latLng.lng(),
    });
  }, [isDrawing, radiusDrawMode, exclusionDrawingMode, onDeleteExclusionPolygon]);

  // Map click - radius-draw + exclusion-draw take priority when active;
  // otherwise polygon points are dropped while polygon drawing mode is
  // ON. When no drawing mode is active a plain map click dismisses any
  // pending-delete overlay.
  const onClick = useCallback((e) => {
    const lat = e.latLng.lat();
    const lng = e.latLng.lng();
    if (radiusDrawMode) {
      if (!radiusCenter) {
        setRadiusCenterLocal([lat, lng]);
        if (onRadiusSetCenter) onRadiusSetCenter(lat, lng);
      } else if (onRadiusFinalize) {
        const sphericalDist = window.google?.maps?.geometry?.spherical?.computeDistanceBetween(
          new window.google.maps.LatLng(radiusCenter[0], radiusCenter[1]),
          new window.google.maps.LatLng(lat, lng),
        );
        onRadiusFinalize(lat, lng, sphericalDist || 0);
      }
      return;
    }
    if (exclusionDrawingMode && onExclusionAddPoint) {
      // Close-on-first-vertex behavior: if user clicks near vertex 0 of
      // the in-progress exclusion (>=3 points placed), close instead of
      // dropping a new vertex. Same threshold as polygon-zone drawing.
      if (exclusionPoints.length >= 3) {
        const first = exclusionPoints[0];
        const dx = lat - first[0];
        const dy = lng - first[1];
        if (dx * dx + dy * dy < 0.0005 * 0.0005) {
          if (onCloseExclusionPolygon) onCloseExclusionPolygon();
          return;
        }
      }
      onExclusionAddPoint(lat, lng);
      return;
    }
    if (isDrawing && onAddPoint) {
      onAddPoint(lat, lng);
      return;
    }
    // Plain click on the map (no drawing mode): close the trash overlay.
    if (pendingDelete) setPendingDelete(null);
  }, [isDrawing, onAddPoint, radiusDrawMode, radiusCenter, onRadiusSetCenter, onRadiusFinalize, pendingDelete, exclusionDrawingMode, exclusionPoints, onExclusionAddPoint, onCloseExclusionPolygon]);

  // Live preview while sizing the radius — RAF-throttled. State is LOCAL
  // to this component so the parent's giant tree doesn't re-render during
  // the drag (that was the source of the original freezing bug). The
  // `onRadiusPreview` callback is still fired (also RAF-throttled) so a
  // far-away readout panel can subscribe, but consumers should avoid
  // doing expensive work in it.
  const radiusRafIdRef = useRef(null);
  const radiusPendingLatLngRef = useRef(null);
  const radiusCenterLatLngRef = useRef(null);
  useEffect(() => {
    if (radiusCenter && window.google?.maps?.LatLng) {
      radiusCenterLatLngRef.current = new window.google.maps.LatLng(
        radiusCenter[0], radiusCenter[1],
      );
    } else {
      radiusCenterLatLngRef.current = null;
    }
  }, [radiusCenter]);
  useEffect(() => () => {
    if (radiusRafIdRef.current) {
      cancelAnimationFrame(radiusRafIdRef.current);
      radiusRafIdRef.current = null;
    }
  }, []);
  const onMouseMove = useCallback((e) => {
    if (!radiusDrawMode || !radiusCenter) return;
    const spherical = window.google?.maps?.geometry?.spherical;
    if (!spherical) return;
    radiusPendingLatLngRef.current = e.latLng;
    if (radiusRafIdRef.current !== null) return; // coalesce
    radiusRafIdRef.current = requestAnimationFrame(() => {
      radiusRafIdRef.current = null;
      const latLng = radiusPendingLatLngRef.current;
      const centerLL = radiusCenterLatLngRef.current;
      if (!latLng || !centerLL) return;
      const meters = spherical.computeDistanceBetween(centerLL, latLng);
      setRadiusPreviewMetersLocal(meters);
      if (onRadiusPreview) onRadiusPreview(meters);
    });
  }, [radiusDrawMode, radiusCenter, onRadiusPreview]);

  const handleDrag = useCallback((index, lat, lng) => {
    if (onDragPoint) {
      onDragPoint(index, lat, lng);
    }
  }, [onDragPoint]);

  // Handle edge click insertion - inserts point at click location
  const handleEdgeInsert = useCallback((afterIndex, position) => {
    if (onInsertPoint) {
      onInsertPoint(afterIndex, { lat: position.lat, lng: position.lng });
    }
  }, [onInsertPoint]);

  // Calculate edges for clickable polylines
  const edges = useMemo(() => {
    if (zonePoints.length < 2) return [];
    const result = [];
    for (let i = 0; i < zonePoints.length; i++) {
      const current = zonePoints[i];
      const next = zonePoints[(i + 1) % zonePoints.length];
      // Only include closing edge if we have 3+ points
      if (i === zonePoints.length - 1 && zonePoints.length < 3) continue;
      result.push({
        start: current,
        end: next,
        afterIndex: i
      });
    }
    return result;
  }, [zonePoints]);

  // Calculate midpoints for visual indicators
  const midpoints = useMemo(() => {
    return edges.map(edge => ({
      position: { 
        lat: (edge.start[0] + edge.end[0]) / 2, 
        lng: (edge.start[1] + edge.end[1]) / 2 
      },
      afterIndex: edge.afterIndex
    }));
  }, [edges]);

  // Convert zone points to Google Maps format
  const polygonPaths = zonePoints.length >= 3 
    ? zonePoints.map(p => ({ lat: p[0], lng: p[1] }))
    : null;

  // Memoize the GoogleMap options so we don't pass a new object reference
  // on every render — otherwise GoogleMap re-applies all settings on each
  // mouse-move (silent killer of frame rate). MUST be declared before the
  // `if (!isLoaded)` early return so React Hooks order stays stable.
  const memoizedMapOptions = useMemo(() => ({
    ...mapOptions,
    draggableCursor: radiusDrawMode ? 'crosshair' : (mapOptions?.draggableCursor || null),
  }), [radiusDrawMode]);

  // Static Circle options — only the `center` and `radius` props change per
  // mouse-move; everything else is stable so it gets memoized once.
  const radiusCircleOptions = useMemo(() => ({
    strokeColor: '#0284c7',
    strokeOpacity: 0.9,
    strokeWeight: 2,
    fillColor: '#0ea5e9',
    fillOpacity: 0.15,
    clickable: false,
    draggable: false,
    editable: false,
    zIndex: 1,
  }), []);

  if (!isLoaded) {
    return (
      <div className="w-full h-full flex items-center justify-center bg-gray-100 rounded-lg">
        <div className="w-8 h-8 border-4 border-purple-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <GoogleMap
      mapContainerStyle={mapContainerStyle}
      center={mapCenter}
      zoom={initialZoom}
      options={memoizedMapOptions}
      onLoad={onLoad}
      onClick={onClick}
      onMouseMove={onMouseMove}
    >
      {/* Grid section overlays - render first (lowest z-index) */}
      {showGrids && gridSections.map((section, idx) => (
        <GridSectionPolygon
          key={`grid-${idx}`}
          section={section}
          colorIndex={idx}
          showLabel={true}
        />
      ))}

      {/* Drawing path - shows line connecting points while drawing */}
      <DrawingPath points={zonePoints} isDrawing={isDrawing} />

      {/* Main zone polygon (visual only) - only shows when NOT drawing (polygon is closed) */}
      {/* Render all delivery-zone polygons.
          - If `allPolygons` is provided we iterate the full list so
            inactive zones are visible too (active = purple, inactive
            = indigo). Each polygon is clickable so the user can pop
            the trash overlay to delete it.
          - Falls back to the legacy single-active rendering when the
            parent doesn't supply `allPolygons` (avoids surprises in
            other consumers of <DeliveryZoneMap>). */}
      {allPolygons
        ? allPolygons.map((poly, idx) => {
            if (!poly || poly.length < 3) return null;
            const paths = poly.map((p) => ({ lat: p[0], lng: p[1] }));
            return (
              <ZonePolygon
                key={`zone-${idx}`}
                paths={paths}
                isActive={idx === activePolygonIndex}
                clickable={!isDrawing && !radiusDrawMode && !exclusionDrawingMode && !!onDeletePolygon}
                onPolygonClick={(e) => handlePolygonClick(idx, e)}
              />
            );
          })
        : polygonPaths && !isDrawing && (
            <ZonePolygon
              paths={polygonPaths}
              isActive
              clickable={!isDrawing && !radiusDrawMode && !exclusionDrawingMode && !!onDeletePolygon}
              onPolygonClick={(e) => handlePolygonClick(activePolygonIndex, e)}
            />
          )}

      {/* Exclusion polygons — drawn INSIDE delivery zones to deselect a
          chunk of houses. Rendered in red at a higher zIndex than the
          purple delivery polygons so the cut-out always wins visually.
          Each is clickable for a floating delete (same UX as zones).
          Closed exclusion polygons only render when at least 3 vertices
          exist — partial in-progress polygons render via the
          DrawingPath + VertexMarker block below. */}
      {exclusionPolygons.map((poly, idx) => {
        if (!poly || poly.length < 3) return null;
        const paths = poly.map((p) => ({ lat: p[0], lng: p[1] }));
        return (
          <ExclusionPolygon
            key={`exclusion-${idx}`}
            paths={paths}
            clickable={!isDrawing && !radiusDrawMode && !exclusionDrawingMode && !!onDeleteExclusionPolygon}
            onPolygonClick={(e) => handleExclusionPolygonClick(idx, e)}
          />
        );
      })}

      {/* In-progress exclusion draw line (red) + vertex markers so the
          admin can click the first vertex to close, same UX as the
          polygon-zone drawing flow. */}
      {exclusionDrawingMode && (
        <DrawingPath points={exclusionPoints} isDrawing={true} color="#dc2626" />
      )}
      {exclusionDrawingMode && exclusionPoints.map((point, idx) => (
        <VertexMarker
          key={`excl-vertex-${idx}`}
          position={{ lat: point[0], lng: point[1] }}
          index={idx}
          onDrag={() => {/* exclusion points aren't draggable in MVP */}}
          isFirstPoint={idx === 0}
          canClose={exclusionPoints.length >= 3}
          onClose={onCloseExclusionPolygon}
        />
      ))}
      
      {/* Clickable edge polylines - ALWAYS available for inserting points */}
      {onInsertPoint && edges.map((edge, idx) => (
        <ClickableEdge
          key={`edge-${idx}`}
          start={edge.start}
          end={edge.end}
          afterIndex={edge.afterIndex}
          onInsert={handleEdgeInsert}
        />
      ))}
      
      {/* Midpoint markers - visual indicators for where to click */}
      {onInsertPoint && midpoints.map((mp, idx) => (
        <MidpointMarker
          key={`midpoint-${idx}`}
          position={mp.position}
          afterIndex={mp.afterIndex}
          onInsert={handleEdgeInsert}
        />
      ))}
      
      {/* Vertex markers - always draggable */}
      {zonePoints.map((point, idx) => (
        <VertexMarker
          key={`vertex-${idx}`}
          position={{ lat: point[0], lng: point[1] }}
          index={idx}
          onDrag={handleDrag}
          isFirstPoint={idx === 0}
          canClose={isDrawing && zonePoints.length >= 3}
          onClose={onClosePolygon}
        />
      ))}
      
      {/* Blocked addresses */}
      {blockedAddresses.map((addr, idx) => (
        addr.lat && addr.lng && (
          <BlockedMarker
            key={`blocked-${idx}`}
            position={{ lat: addr.lat, lng: addr.lng }}
            address={addr.address}
          />
        )
      ))}
      
      {/* Counted house location dots - canvas overlay for performance */}
      {houseLocations.length > 0 && (
        <HouseDotsOverlay map={mapRef.current} houseLocations={houseLocations} />
      )}

      {/* ---- Radius Draw live preview ----
          1) Once the center has been dropped, draw a translucent sky-blue
             circle that grows/shrinks with the mouse position.
          2) Drop a small center marker so the admin always sees exactly
             where they dropped point #1.
          3) Anchor a small "X.XX mi" label at the eastern edge of the
             circle — it tracks live with the radius so the number visibly
             grows as the user drags outward.
      */}
      {radiusDrawMode && radiusCenter && radiusPreviewMeters > 0 && (
        <>
          <Circle
            center={{ lat: radiusCenter[0], lng: radiusCenter[1] }}
            radius={radiusPreviewMeters}
            options={radiusCircleOptions}
          />
          {(() => {
            // Compute the point at due-east bearing for label placement.
            // Use spherical geometry so the label sits on the visible edge
            // at any zoom / latitude.
            if (!window.google?.maps?.geometry?.spherical) return null;
            const edge = window.google.maps.geometry.spherical.computeOffset(
              new window.google.maps.LatLng(radiusCenter[0], radiusCenter[1]),
              radiusPreviewMeters,
              90, // east
            );
            const miles = (radiusPreviewMeters / 1609.344).toFixed(2);
            return (
              <OverlayView
                position={{ lat: edge.lat(), lng: edge.lng() }}
                mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
              >
                <div
                  className="px-2 py-1 rounded-md shadow-md bg-white text-black border border-gray-400 text-xs font-bold tabular-nums whitespace-nowrap pointer-events-none"
                  style={{ transform: 'translate(8px, -50%)' }}
                  data-testid="radius-draw-map-label"
                >
                  {miles} mi
                </div>
              </OverlayView>
            );
          })()}
        </>
      )}
      {radiusDrawMode && radiusCenter && (
        <Marker
          position={{ lat: radiusCenter[0], lng: radiusCenter[1] }}
          icon={{
            path: window.google?.maps?.SymbolPath?.CIRCLE,
            scale: 6,
            fillColor: '#0284c7',
            fillOpacity: 1,
            strokeColor: '#ffffff',
            strokeWeight: 2,
          }}
          clickable={false}
          zIndex={1000}
        />
      )}

      {/* Floating trash button — shown after the user clicks an existing
          polygon (and no drawing mode is active). Click the button to
          delete that polygon; click the blank map to dismiss. The button
          intentionally lives in OVERLAY_MOUSE_TARGET so it captures its
          own click instead of bubbling to the map. */}
      {pendingDelete && (
        (pendingDelete.kind === 'exclusion' ? onDeleteExclusionPolygon : onDeletePolygon)
      ) && (
        <OverlayView
          position={{ lat: pendingDelete.lat, lng: pendingDelete.lng }}
          mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
        >
          <div
            style={{ transform: 'translate(-50%, -100%)' }}
            className="pointer-events-auto"
          >
            <button
              type="button"
              onClick={(ev) => {
                ev.stopPropagation();
                const idx = pendingDelete.idx;
                const kind = pendingDelete.kind || 'delivery';
                setPendingDelete(null);
                if (kind === 'exclusion' && onDeleteExclusionPolygon) {
                  onDeleteExclusionPolygon(idx);
                } else if (onDeletePolygon) {
                  onDeletePolygon(idx);
                }
              }}
              className={
                'flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-white text-xs font-semibold shadow-lg border-2 border-white whitespace-nowrap focus:outline-none focus:ring-2 ' +
                (pendingDelete.kind === 'exclusion'
                  ? 'bg-red-700 hover:bg-red-800 focus:ring-red-300'
                  : 'bg-rose-600 hover:bg-rose-700 focus:ring-rose-300')
              }
              data-testid={
                pendingDelete.kind === 'exclusion'
                  ? `delete-exclusion-on-map-btn-${pendingDelete.idx}`
                  : `delete-zone-on-map-btn-${pendingDelete.idx}`
              }
              aria-label={
                pendingDelete.kind === 'exclusion'
                  ? `Delete exclusion zone ${pendingDelete.idx + 1}`
                  : `Delete zone ${pendingDelete.idx + 1}`
              }
              title={
                pendingDelete.kind === 'exclusion'
                  ? `Delete Exclusion ${pendingDelete.idx + 1}`
                  : `Delete Zone ${pendingDelete.idx + 1}`
              }
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="13"
                height="13"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="M3 6h18" />
                <path d="M19 6l-2 14H7L5 6" />
                <path d="M10 11v6" />
                <path d="M14 11v6" />
                <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
              </svg>
              {pendingDelete.kind === 'exclusion'
                ? `Delete Exclusion ${pendingDelete.idx + 1}`
                : `Delete Zone ${pendingDelete.idx + 1}`}
            </button>
          </div>
        </OverlayView>
      )}
    </GoogleMap>
  );
};

export default memo(DeliveryZoneMap);
